import React from 'react';
import draftingFlyer from '../assets/images/services/arch_floor_plan.png';
import systemDesignFlyer from '../assets/images/system_design.png';
import takeoffFlyer from '../assets/images/takeoff.png';
import blogCoverImage from '../assets/images/services/arch_floor_plan.png';

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  content: React.ReactNode;
  author: string;
  date: string;
  coverImage: string;
  metaTitle: string;
  metaDescription: string;
}

export const blogs: BlogPost[] = [
  {
    id: '1',
    slug: 'drafting-support-for-low-voltage-integrators',
    title: 'Drafting Support for Low-Voltage Integrators: Drafting, Design, Programming & Takeoffs',
    excerpt: 'Drafting by DMI provides drafting, system design, programming, takeoffs, estimating, and proposal support for low-voltage integrators.',
    author: 'DMI Team',
    date: 'September 19, 2026',
    coverImage: blogCoverImage,
    metaTitle: 'Low-Voltage Design & Drafting Support | Drafting by DMI',
    metaDescription: 'Drafting by DMI provides drafting, system design, programming, takeoffs, estimating, and proposal support for low-voltage integrators.',
    content: (
      <div className="space-y-6 text-gray-800 dark:text-gray-200">
        <p className="text-xl leading-relaxed text-gray-600 dark:text-gray-300 font-medium">
          A successful low-voltage project starts long before equipment reaches the job site.
        </p>

        <p className="leading-relaxed">
          For low-voltage integrators, interpreting plans, developing system layouts, and building accurate takeoffs can quickly become a bottleneck. That's where Drafting by DMI comes in. We provide design, drafting, programming, takeoff, and proposal support—helping teams increase capacity without adding in-house headcount.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4">Our Services</h3>
        <ul className="list-disc pl-5 space-y-2 mb-6">
          <li><strong>Drafting & Design Support:</strong> Prewire, device layout plans, rack elevations, and coordination-ready drawing packages.</li>
          <li><strong>System Design & Programming:</strong> Full low-voltage system design and Lutron/Savant programming support.</li>
          <li><strong>Takeoffs & Proposal Building:</strong> Detailed quantity takeoffs, labor-hour estimating, and client-ready proposal packages.</li>
        </ul>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 mt-10 border border-gray-100 dark:border-gray-700 text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Let's Work Together</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Need an extension of your engineering and estimating team? We're currently offering a complimentary takeoff or design request for qualified companies.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href="mailto:drafting@digitalmediainnovations.com" className="px-6 py-3 bg-[#0d9488] hover:bg-[#0b796f] text-white font-medium rounded-xl transition-colors shadow-sm">
              Email Us Today
            </a>
            <a href="tel:3038731100" className="flex items-center text-gray-700 dark:text-gray-300 hover:text-[#0d9488] font-medium transition-colors">
              <i className="ri-phone-line mr-2 text-xl"></i> (303) 873-1100
            </a>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: '2',
    slug: 'comprehensive-low-voltage-plan-modern-smart-home',
    title: 'Comprehensive Low Voltage Plan for a Modern Smart Home',
    excerpt: 'A detailed look at how structured technology planning can transform a house into a connected, secure, and entertainment-focused smart home environment.',
    author: 'DMI AV Design Team',
    date: 'September 27, 2026',
    coverImage: systemDesignFlyer,
    metaTitle: 'Comprehensive Low Voltage Plan for a Modern Smart Home',
    metaDescription: 'Discover the importance of a comprehensive low voltage plan for modern smart homes. Learn how to coordinate networking, audio, TV, security, and smart control.',
    content: (
      <div className="space-y-6 text-gray-800 dark:text-gray-200">
        <p className="text-xl leading-relaxed text-gray-600 dark:text-gray-300 font-medium">
          Designing a Connected, Secure, and Entertainment-Focused Home
        </p>

        <p className="leading-relaxed">
          A well-planned low voltage system is the foundation of a modern smart home. By coordinating networking, audio, surveillance, and smart-home control during the planning stage, the installation can be completed with organized cabling and clearly defined equipment locations.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4">Key Infrastructure Highlights</h3>
        <ul className="list-disc pl-5 space-y-2 mb-6">
          <li><strong>Structured Network & CAT6:</strong> Centralized network rack with Ubiquiti U7 Pro access points for seamless connectivity.</li>
          <li><strong>Whole-Home Audio & Entertainment:</strong> Sonance in-ceiling and outdoor speakers, alongside dedicated home theater and projection systems.</li>
          <li><strong>Smart Home & Security:</strong> Control4 T4 touchscreens, Lutron motorized shades, and G6 Bullet Pro cameras for comprehensive coverage.</li>
        </ul>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 mt-10 border border-gray-100 dark:border-gray-700 text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Ready to Plan Your Smart Home?</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            A detailed low voltage plan is the roadmap to a successful installation. Let our design experts build the foundation for your connected home.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href="mailto:drafting@digitalmediainnovations.com" className="px-6 py-3 bg-[#0d9488] hover:bg-[#0b796f] text-white font-medium rounded-xl transition-colors shadow-sm">
              Schedule a Consultation
            </a>
            <a href="tel:3038731100" className="flex items-center text-gray-700 dark:text-gray-300 hover:text-[#0d9488] font-medium transition-colors">
              <i className="ri-phone-line mr-2 text-xl"></i> (303) 873-1100
            </a>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: '3',
    slug: 'upgrading-legacy-lutron-homeworks-illumination-systems',
    title: 'Upgrading Legacy Lutron HomeWorks Illumination Systems',
    excerpt: 'Legacy Lutron HomeWorks Illumination systems can become challenging to maintain. Upgrading to the modern HomeWorks QSX platform requires proper planning and drafting for a smooth transition.',
    author: 'DMI Team',
    date: 'October 5, 2026',
    coverImage: blogCoverImage,
    metaTitle: 'Upgrading Legacy Lutron HomeWorks | Drafting by DMI',
    metaDescription: 'Proper planning and device compatibility verification help ensure a smooth transition to Lutron HomeWorks QSX with minimal disruption.',
    content: (
      <div className="space-y-6 text-gray-800 dark:text-gray-200">
        <p className="text-xl leading-relaxed text-gray-600 dark:text-gray-300 font-medium">
          Transition smoothly to modern platforms like HomeWorks QSX.
        </p>

        <p className="leading-relaxed">
          Upgrading legacy Lutron HomeWorks Illumination systems to the modern QSX platform requires meticulous planning. Without clear, updated as-built drawings, installation teams face costly delays. At Drafting by DMI, we provide the critical documentation needed before the first wire is touched.
        </p>

        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4">Our Upgrade Drafting Services</h3>
        <ul className="list-disc pl-5 space-y-2 mb-6">
          <li><strong>Device Compatibility Verification:</strong> Mapping existing keypads and dimmers to cross-reference QSX compatibility.</li>
          <li><strong>Panel Schedules & Load Planning:</strong> Detailed schedules reflecting the new system's load distribution.</li>
          <li><strong>Wiring Schematics:</strong> Updated layouts guiding integrators clearly on retrofitting requirements.</li>
        </ul>

        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-2xl p-8 mt-10 border border-gray-100 dark:border-gray-700 text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">Start Your Upgrade Project</h2>
          <p className="mb-6 text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Ensure a seamless transition for the homeowner with accurate, detailed documentation.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a href="mailto:drafting@digitalmediainnovations.com" className="px-6 py-3 bg-[#0d9488] hover:bg-[#0b796f] text-white font-medium rounded-xl transition-colors shadow-sm">
              Contact Our Design Team
            </a>
            <a href="tel:3038731100" className="flex items-center text-gray-700 dark:text-gray-300 hover:text-[#0d9488] font-medium transition-colors">
              <i className="ri-phone-line mr-2 text-xl"></i> (303) 873-1100
            </a>
          </div>
        </div>
      </div>
    ),
  }
].sort((a, b) => Number(b.id) - Number(a.id));
